import React, { useState } from "react";
import { getDailySalesReport } from "../../services/api";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";
import { toDDMMYYYY, toDDMMYYYYHHMM } from "../../utils/dateUtils";

// ── Reference maps ────────────────────────────────────────────────────────────

const DOC_TYPE_LABELS: Record<string, string> = {
  TKT: "Ticket",
  MCO: "Misc Charges Order",
  EMD: "Electronic Misc Doc",
  MSR: "Misc Sales Receipt",
  RFD: "Refund",
  BAG: "Excess Baggage",
  PTA: "Prepaid Ticket Advice",
  TUR: "Tour Order",
  VCH: "Voucher",
};

const FOP_LABELS: Record<string, string> = {
  CA: "Cash",
  CC: "Credit Card",
  CK: "Check",
  ET: "Exchange Ticket",
  PT: "Pre-Paid Ticket",
  TV: "Travel Voucher",
  BT: "Travel Bank",
  GR: "Government Bill",
  IN: "Involuntary Rerouting",
  CT: "Certificate",
  RA: "Revenue Accounting",
  XX: "Other",
};

// ── Types ─────────────────────────────────────────────────────────────────────

type SalesPayment = {
  fopCode: string;
  currencyCode: string;
  amount: number;
};

type SalesIssuance = {
  agentSine: string;
  documentType: string;
  international: boolean;
  issueTime: string;
  pnrLocator: string;
  stockItemCount: number;
  ticketPrinter: string;
  ticketStock: number;
  ticketStockCode?: string;
  eTicketNumber: string;
  isEticket: boolean;
  usedCouponCount: number;
  indicatorTwo?: string;
  passengerGivenName: string;
  passengerSurname: string;
  route?: string;
  commission?: number;
  payments: SalesPayment[];
};

type SalesReportData = {
  success: boolean;
  date: string;
  pcc: string;
  agencyName: string;
  createDateTime: string;
  totalIssuances: number;
  issuances: SalesIssuance[];
  error?: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, code: string): string {
  return `${code} ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format "HHMM" or "HH:MM" into "HH:MM" */
function formatTime(raw?: string): string {
  if (!raw) return "—";
  const s = raw.replace(/[^0-9]/g, "");
  if (s.length === 4) return `${s.slice(0, 2)}:${s.slice(2)}`;
  if (s.length === 3) return `0${s[0]}:${s.slice(1)}`;
  return raw;
}

function todayISO(): string {
  return new Date().toISOString().split("T")[0];
}

function computeTotalsByCurrency(issuances: SalesIssuance[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const iss of issuances) {
    for (const p of iss.payments) {
      totals[p.currencyCode] = (totals[p.currencyCode] ?? 0) + p.amount;
    }
  }
  return totals;
}

// ── All distinct document types present in the data ──────────────────────────

function docTypesInData(issuances: SalesIssuance[]): string[] {
  const seen = new Set<string>();
  for (const i of issuances) seen.add(i.documentType);
  return Array.from(seen).sort();
}

// ── CSV Export ───────────────────────────────────────────────────────────────

function exportToCSV(issuances: SalesIssuance[], reportDate: string, pcc: string): void {
  const headers = ["Time", "Passenger", "PNR", "E-Ticket", "Type", "Route", "Intl/Dom", "Agent", "Currency", "Amount", "FOP"];
  const rows = issuances.flatMap((iss) => {
    const passenger = iss.passengerSurname && iss.passengerGivenName
      ? `${iss.passengerSurname}/${iss.passengerGivenName}`
      : iss.passengerSurname || iss.passengerGivenName || "";
    if (iss.payments.length === 0) {
      return [[iss.issueTime, passenger, iss.pnrLocator, iss.eTicketNumber, iss.documentType, iss.route ?? "", iss.international ? "Intl" : "Dom", iss.agentSine, "", "", ""]];
    }
    return iss.payments.map((p) => [
      iss.issueTime, passenger, iss.pnrLocator, iss.eTicketNumber, iss.documentType,
      iss.route ?? "", iss.international ? "Intl" : "Dom", iss.agentSine,
      p.currencyCode, p.amount.toFixed(2), p.fopCode,
    ]);
  });
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `sales_report_${pcc || "report"}_${reportDate}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Summary computation ──────────────────────────────────────────────────────

function computeSummary(issuances: SalesIssuance[]): { totalAmount: Record<string, number>; totalIssuances: number; intlCount: number; domCount: number } {
  const totalAmount: Record<string, number> = {};
  let intlCount = 0;
  let domCount = 0;
  for (const iss of issuances) {
    if (iss.international) intlCount++; else domCount++;
    for (const p of iss.payments) {
      totalAmount[p.currencyCode] = (totalAmount[p.currencyCode] ?? 0) + p.amount;
    }
  }
  return { totalAmount, totalIssuances: issuances.length, intlCount, domCount };
}

// ── Component ─────────────────────────────────────────────────────────────────

const SalesReports: React.FC = () => {
  const [date, setDate] = useState<string>(todayISO());
  const [endDate, setEndDate] = useState<string>("");
  const [pcc, setPcc] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<SalesReportData | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [docTypeFilter, setDocTypeFilter] = useState<string>("ALL");

  const handleFetch = async () => {
    if (!date) return;
    setLoading(true);
    setFetchError(null);
    setReport(null);
    setDocTypeFilter("ALL");
    try {
      const data = await getDailySalesReport(date, endDate.trim() || undefined, pcc.trim() || undefined);
      if (!data.success) {
        setFetchError(data.error ?? "Unknown error from server.");
      } else {
        setReport(data as SalesReportData);
      }
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.errors?.[0]?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to fetch report."
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredIssuances =
    report?.issuances.filter(
      (i) => docTypeFilter === "ALL" || i.documentType === docTypeFilter
    ) ?? [];

  const currencyTotals = report ? computeTotalsByCurrency(report.issuances) : {};
  const availableDocTypes = report ? docTypesInData(report.issuances) : [];

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* ── Page header ── */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">Daily Sales Report</h1>
        <p className="text-sm text-gray-500 mt-1">
          Ticket issuance summary from Sabre DailySalesReportLLSRQ.
        </p>
      </div>

      {/* ── Query form ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Date
          </label>
          <DatePickerWithValidation
            selected={date}
            onSelect={setDate}
            maxDate={todayISO()}
            placeholder="dd/mm/yyyy"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            End Date{" "}
            <span className="font-normal normal-case text-gray-400">
              (optional)
            </span>
          </label>
          <DatePickerWithValidation
            selected={endDate || undefined}
            onSelect={setEndDate}
            minDate={date}
            maxDate={todayISO()}
            placeholder="dd/mm/yyyy"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            PCC{" "}
            <span className="font-normal normal-case text-gray-400">
              (optional)
            </span>
          </label>
          <input
            type="text"
            value={pcc}
            onChange={(e) => setPcc(e.target.value.toUpperCase())}
            placeholder="e.g. G3SL"
            maxLength={8}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-36 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button
          onClick={handleFetch}
          disabled={loading || !date}
          className="bg-primary text-white font-semibold px-6 py-2 rounded-full hover:bg-blue-800 transition text-sm disabled:opacity-50"
        >
          {loading ? "Loading…" : "Fetch Report"}
        </button>
      </div>

      {/* ── Error ── */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">
          {fetchError}
        </div>
      )}

      {/* ── Results ── */}
      {report && (
        <>
          {/* Report meta */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Agency</span>
              <p className="font-bold text-heading text-base">{report.agencyName || "—"}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">PCC</span>
              <p className="font-semibold text-heading">{report.pcc}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Report Date</span>
              <p className="font-semibold text-heading">{toDDMMYYYY(report.date) || "—"}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Created</span>
              <p className="font-semibold text-heading">
                {toDDMMYYYYHHMM(report.createDateTime) || "—"}
              </p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Total Issuances</span>
              <p className="font-bold text-heading text-base">{report.totalIssuances}</p>
            </div>
          </div>

          {/* Currency totals */}
          {Object.keys(currencyTotals).length > 0 && (
            <div className="flex flex-wrap gap-3 mb-4">
              {Object.entries(currencyTotals).map(([ccy, amt]) => (
                <div
                  key={ccy}
                  className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-3 text-center min-w-[120px]"
                >
                  <p className="text-xs font-semibold text-blue-500 uppercase">{ccy}</p>
                  <p className="text-lg font-bold text-heading">
                    {amt.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Doc type filter tabs */}
          {report.issuances.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setDocTypeFilter("ALL")}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                  docTypeFilter === "ALL"
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary"
                }`}
              >
                All ({report.issuances.length})
              </button>
              {availableDocTypes.map((dt) => {
                const count = report.issuances.filter((i) => i.documentType === dt).length;
                return (
                  <button
                    key={dt}
                    onClick={() => setDocTypeFilter(dt)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                      docTypeFilter === dt
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary"
                    }`}
                  >
                    {dt} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Export CSV button */}
          {report.issuances.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => exportToCSV(filteredIssuances, report.date, report.pcc)}
                className="bg-white border border-gray-300 text-gray-700 font-semibold px-5 py-2 rounded-full hover:border-primary hover:text-primary transition text-sm"
              >
                Export CSV
              </button>
            </div>
          )}

          {/* Empty state */}
          {report.issuances.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-400 text-sm">
              No issuances found for this date.
            </div>
          )}

          {/* Issuances table */}
          {filteredIssuances.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        Time
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        Passenger
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        PNR
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        E-Ticket
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        Type
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        Agent
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        Amount
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        FOP
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIssuances.map((iss, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-100 hover:bg-gray-50 transition"
                      >
                        {/* Time */}
                        <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">
                          {formatTime(iss.issueTime)}
                        </td>

                        {/* Passenger */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-semibold text-heading text-xs">
                            {iss.passengerSurname && iss.passengerGivenName
                              ? `${iss.passengerSurname}/${iss.passengerGivenName}`
                              : iss.passengerSurname || iss.passengerGivenName || "—"}
                          </span>
                        </td>

                        {/* PNR */}
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-heading whitespace-nowrap">
                          {iss.pnrLocator || "—"}
                        </td>

                        {/* E-Ticket */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {iss.eTicketNumber ? (
                            <div>
                              <span className="font-mono text-xs text-heading">{iss.eTicketNumber}</span>
                              {iss.isEticket && (
                                <span className="ml-1.5 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-medium">
                                  E-TKT
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Doc type + Intl/Dom */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold rounded px-2 py-0.5">
                              {iss.documentType}
                            </span>
                            <span
                              className={`inline-block text-[10px] font-semibold rounded px-1.5 py-0.5 ${
                                iss.international
                                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                                  : "bg-green-50 text-green-600 border border-green-200"
                              }`}
                            >
                              {iss.international ? "INTL" : "DOM"}
                            </span>
                          </div>
                          {DOC_TYPE_LABELS[iss.documentType] && (
                            <span className="text-gray-400 text-[10px] block mt-0.5">
                              {DOC_TYPE_LABELS[iss.documentType]}
                            </span>
                          )}
                        </td>

                        {/* Agent */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs font-mono font-semibold text-gray-700">
                            {iss.agentSine || "—"}
                          </span>
                          {iss.ticketPrinter && (
                            <span className="block text-[10px] text-gray-400 mt-0.5">
                              Printer: {iss.ticketPrinter}
                            </span>
                          )}
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {iss.payments.length > 0 ? (
                            <div className="flex flex-col items-end gap-0.5">
                              {iss.payments.map((p, pi) => (
                                <span key={pi} className="font-semibold text-heading text-xs">
                                  {formatCurrency(p.amount, p.currencyCode)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                          {iss.commission != null && iss.commission > 0 && (
                            <span className="block text-[10px] text-gray-400 mt-0.5">
                              Comm: {iss.commission}
                            </span>
                          )}
                        </td>

                        {/* FOP */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {iss.payments.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {iss.payments.map((p, pi) => (
                                <span
                                  key={pi}
                                  className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold rounded px-2 py-0.5"
                                  title={FOP_LABELS[p.fopCode] ?? p.fopCode}
                                >
                                  {p.fopCode}{FOP_LABELS[p.fopCode] ? ` (${FOP_LABELS[p.fopCode]})` : ""}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Summary row */}
                  <tfoot>
                    {(() => {
                      const summary = computeSummary(filteredIssuances);
                      const currencyEntries = Object.entries(summary.totalAmount);
                      return (
                        <tr className="bg-gray-50 border-t-2 border-gray-300">
                          <td className="px-4 py-3 text-xs font-bold text-heading uppercase" colSpan={5}>
                            Totals ({summary.totalIssuances} issuances)
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">—</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs font-semibold text-gray-600">
                              {summary.intlCount} Intl / {summary.domCount} Dom
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">—</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex flex-col items-end gap-0.5">
                              {currencyEntries.map(([ccy, amt]) => (
                                <span key={ccy} className="font-bold text-heading text-xs">
                                  {formatCurrency(amt, ccy)}
                                </span>
                              ))}
                              {currencyEntries.length === 0 && (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">—</td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SalesReports;
